import { useEffect, useMemo, useState } from 'react'
import { Apple, ArrowRight, Download, Loader2, Monitor } from 'lucide-react'
import api from '../../utils/api'

function AdIqiyiDownloadButtons({ theme = 'rose', trackingParams = null }) {
  const [downloads, setDownloads] = useState([])
  const [loading, setLoading] = useState(true)
  const [downloading, setDownloading] = useState(null)

  // 从 URL 路径提取 route：/adiqiyi2 → adiqiyi2，/adiqiyi → adiqiyi
  // 注意：adiqiyi2 必须放在 adiqiyi 之前，否则 /adiqiyi2 会被前缀误匹配成 adiqiyi
  const route = useMemo(() => {
    const pathname = window.location.pathname
    const match = pathname.match(/\/(adiqiyi2|adiqiyi)/)
    return match ? match[1] : 'adiqiyi'
  }, [])

  useEffect(() => {
    let mounted = true

    const fetchDownloads = async () => {
      try {
        const response = await api.get('/downloads')
        if (mounted && response.data.success) {
          setDownloads(response.data.downloads || [])
        }
      } catch (error) {
        console.error('获取下载列表失败:', error)
      } finally {
        if (mounted) setLoading(false)
      }
    }

    fetchDownloads()
    return () => {
      mounted = false
    }
  }, [])

  const palette = useMemo(() => {
    const map = {
      rose: {
        card: 'border-rose-100 bg-white hover:border-rose-300 hover:shadow-[0_24px_60px_-30px_rgba(244,63,94,0.28)]',
        icon: 'bg-rose-50 text-rose-600',
        button: 'border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100'
      },
      cyan: {
        card: 'border-cyan-100 bg-white hover:border-cyan-300 hover:shadow-[0_24px_60px_-30px_rgba(34,211,238,0.45)]',
        icon: 'bg-cyan-50 text-cyan-600',
        button: 'border-cyan-200 bg-cyan-50 text-cyan-700 hover:bg-cyan-100'
      }
    }
    return map[theme] || map.rose
  }, [theme])

  const items = [
    { id: 'windows', label: 'Windows 版本', helper: '适合 Windows 10/11 用户', icon: Monitor },
    { id: 'macos-intel', label: 'Mac Intel 版本', helper: '适合 Intel 芯片 Mac', icon: Apple },
    { id: 'macos-apple', label: 'Mac Apple 版本', helper: '适合 Apple Silicon 芯片 Mac', icon: Apple }
  ].map((item) => {
    const match = downloads.find((download) => download.id === item.id)
    return {
      ...item,
      version: match?.version,
      available: !!match?.file_path
    }
  })

  const handleDownload = (id) => {
    setDownloading(id)
    const p = trackingParams || {}
    const qs = new URLSearchParams()
    if (p.orderPlanId) qs.set('order_plan_id', p.orderPlanId)
    if (p.creativeId) qs.set('creative_id', p.creativeId)
    if (p.impressId) qs.set('impress_id', p.impressId)
    if (p.requestTime) qs.set('request_time', p.requestTime)
    if (p.sign) qs.set('sign', p.sign)
    const query = qs.toString() ? `?${qs.toString()}` : ''
    const finalUrl = `/api/${route}/download/${encodeURIComponent(id)}${query}`
    window.location.href = finalUrl
    window.setTimeout(() => setDownloading(null), 1200)
  }

  return (
    <div className="grid gap-4 md:grid-cols-3">
      {loading ? (
        <div className="col-span-full flex items-center justify-center rounded-3xl border border-slate-200 bg-white px-6 py-12 text-slate-600 shadow-sm">
          <Loader2 className="mr-3 h-6 w-6 animate-spin" />
          正在获取下载信息...
        </div>
      ) : (
        items.map((item) => {
          const Icon = item.icon
          const isBusy = downloading === item.id
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => handleDownload(item.id)}
              disabled={isBusy || !item.available}
              className={`group rounded-3xl border p-6 text-left text-slate-900 transition duration-300 ${palette.card} ${
                !item.available ? 'cursor-not-allowed opacity-60' : ''
              }`}
            >
              <div className={`mb-5 inline-flex rounded-2xl p-3 ${palette.icon}`}>
                <Icon className="h-6 w-6" />
              </div>
              <div className="mb-2 text-lg font-semibold">{item.label}</div>
              <div className="mb-5 text-sm leading-6 text-slate-500">
                {item.available ? item.helper : `${item.helper} · 暂无可下载文件`}
              </div>
              <div className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm ${palette.button}`}>
                {isBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                立即下载
                {!isBusy && <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />}
              </div>
            </button>
          )
        })
      )}
    </div>
  )
}

export default AdIqiyiDownloadButtons
